/** @jsxRuntime classic */
/** @jsx jsx */
import { css, Global, jsx } from "@emotion/core";
import React, { useState, useEffect, ComponentProps } from "react";
import { useSelector, useDispatch } from "react-redux";
import firebase from "firebase/app";
import { Store } from "../../store";
import QrReader from "react-qr-reader";
import { modalActions } from "../../modules/modal";
import { getSlashSeparatedDateTimeStringFromFbTimestamp } from "utility";
import {
  updateFirestoreDoc,
  fetchFirestoreDocument,
  fetchFirestoreCollectionMap,
} from "../../utility/firebase";
import {
  decodeTicketQRCodeValue,
  getVenuePayPrice,
  SHIPPING_PLACE_CODE,
} from "utility/ticket";
import * as z from "zod/mini";
import { loadingActions } from "../../modules/loading";
import { useIssuedTicketMap } from "./useIssuedTickets";
import { getReaderId } from "./qrcodeReaderId";
import { useSelectedRedeemTicketIdsAndEventIds } from "./useSelectedRedeemTicketIds";
import {
  playSuccessSound,
  useUnlockAudioOnUserGesture,
} from "./useSuccessSound";
import { useI18n } from "hooks/i18n/i18n";
import { useQuery } from "utility/useQuery";
import { QrcodeInfo } from "../../components/qrcode/QrcodeInfo";
import { Button } from "components/atoms/button/Button";
import { QrcodePrint } from "components/qrcode/QrcodePrint";
import type {
  ActiveTransaction,
  Event,
  IssuedTicket,
  ProductData,
} from "@spwn/types/firebase/firestore";
import { dbFirestore } from "firestore";
import { FullScreenDialog } from "components/common/Modal";

const isOfflineError = (error: unknown): boolean =>
  error instanceof Error &&
  "code" in error &&
  (error as { code: unknown }).code === "unavailable";

const QrcodeReader: React.FC = () => {
  const { t } = useI18n();
  class DecordingTypeError extends TypeError {}
  const dispatch = useDispatch();
  useUnlockAudioOnUserGesture();
  const [uid, setLocalUid] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      setLocalUid(user?.uid ?? null);
      setIsAuthReady(true);
    });
    return unsubscribe;
  }, []);
  const query = useQuery();
  const queryEventId = query.get("eventId");
  const queryTicketId = query.get("ticketId");
  const {
    data: selectedRedeemTicketIdsAndEventIds,
    error: redeemTicketIdsError,
  } = useSelectedRedeemTicketIdsAndEventIds();
  const selectedRedeemTicketIds = selectedRedeemTicketIdsAndEventIds?.ticketIds;
  const selectedRedeemEventIds = selectedRedeemTicketIdsAndEventIds?.eventIds;

  const isOpenError = useSelector((state: Store) => state.modal.isOpenError);

  const { issuedTicketMap, error: issuedTicketsError } = useIssuedTicketMap(
    selectedRedeemEventIds
  );

  const classesQrcodeStyle = qrcodeStyle();

  const [qrcodeRowData, setQrcodeRowData] = useState<string>(""); //base64の状態 qrcodeReaderのパッケージで必要
  const [qrcodeJson, setQrcodeJson] =
    useState<ReturnType<typeof makePathDecodedJSON>>(); //qrcodeRowDataをmapに直したもの
  const [eventStatus, setEventStatus] = useState<{
    eventName: string;
    eventDate: string;
  }>();
  const [purchasedGoodsStatus, setPurchasedGoodsStatus] =
    useState<ComponentProps<typeof QrcodeInfo>["purchasedGoodsStatus"]>();
  const [isReadError, setIsReadError] = useState<boolean>(false); //qrcodeが指定されたフォーマットに即しているか モギリの際に使用

  const [isFireStorePermissionError, setIsFireStorePermissionError] =
    useState<boolean>(false);
  const [isPrint, setIsPrint] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogMessage, setDialogMessage] = useState<string>("");

  useEffect(() => {
    if (isAuthReady && !uid) {
      dispatch(modalActions.toggleLoginMethod());
    }
  }, [isAuthReady, uid, dispatch]);

  useEffect(() => {
    if (redeemTicketIdsError && !isOpenError) {
      setDialogMessage("❌もぎりグループの取得に失敗しました");
      setIsDialogOpen(true);
    }
  }, [redeemTicketIdsError, isOpenError, dispatch]);

  useEffect(() => {
    if (issuedTicketsError && !isOpenError) {
      setDialogMessage("❌チケット情報の同期に失敗しました");
      setIsDialogOpen(true);
    }
  }, [issuedTicketsError, isOpenError, dispatch]);

  // @ts-expect-error TS7006
  const handleError = (err) => {
    console.error(err);
  };

  // @ts-expect-error TS7006
  const handleScan = (data) => {
    if (data && !isDialogOpen) {
      setQrcodeRowData(data);
    }
  };

  useEffect(() => {
    if (qrcodeRowData !== "") {
      (async () => {
        setIsFireStorePermissionError(false);
        try {
          dispatch(
            loadingActions.toggleLoading({ msg: t("qrcode.loadingQrcode") })
          );
          const data = makePathDecodedJSON(qrcodeRowData);
          if (data) {
            setQrcodeJson(data);
            await fetchTicketData(data);
            if (data.ticketType === "isChecked") {
              await checkTicket(data);
            }
          }
        } catch (e) {
          setDialogMessage(`❌${t("cart.msg.unexpectedError")}`);
          setIsDialogOpen(true);
          console.log(e);
        } finally {
          dispatch(loadingActions.toggleLoading({}));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrcodeRowData]); // TODO

  const initialState = () => {
    setQrcodeRowData("");
    // @ts-expect-error TS2345
    setQrcodeJson(null);
    // @ts-expect-error TS2345
    setEventStatus(null);
    // @ts-expect-error TS2345
    setPurchasedGoodsStatus(null);
    setIsReadError(false);
  };

  const fetchTicketData = async (ticketData: {
    ticketPath: string;
    eventId: string;
    ticketType: string;
    authCode: string;
    orderId: string;
  }) => {
    if (ticketData.ticketType !== "isGoods") {
      return;
    }
    try {
      const eventTicketData = await fetchFirestoreDocument<IssuedTicket>(
        ticketData.ticketPath
      );
      if (eventTicketData) {
        fetchEventInfo(ticketData.eventId);
        if (ticketData.ticketType === "isGoods") {
          const activetranData =
            await fetchFirestoreDocument<ActiveTransaction>(
              `settlement/${eventTicketData.owner}/activeTransactions/${ticketData.orderId}`
            );
          const goodsCollectionMap =
            await fetchFirestoreCollectionMap<ProductData>(
              `/goods/${ticketData.eventId}/goods`
            );
          if (
            !activetranData.isPurchased ||
            activetranData.status !== "PAYSUCCESS"
          ) {
            setDialogMessage(`❌${t("qrcode.errorQrcode3")}`);
            setIsDialogOpen(true);
            return;
          }
          if (activetranData.isCancelled) {
            setDialogMessage(`❌${t("qrcode.errorQrcode4")}`);
            setIsDialogOpen(true);
            return;
          }
          if (goodsCollectionMap && activetranData) {
            const goodsList = Object.keys(
              // @ts-expect-error TS2532
              activetranData.eventProductCountMap[ticketData.eventId].goods
            ).map((gid) => {
              return {
                // @ts-expect-error TS2532
                name: goodsCollectionMap[gid].name,
                // @ts-expect-error TS2532
                subClassName: goodsCollectionMap[gid].subClassName,
                // @ts-expect-error TS18047
                count: activetranData.productCountMap[gid],
                // @ts-expect-error TS2345
                price_jpy: getVenuePayPrice(goodsCollectionMap[gid]),
                // @ts-expect-error TS2532
                priority: goodsCollectionMap[gid].priority,
                // @ts-expect-error TS2532
                place: goodsCollectionMap[gid].place,
              };
            });
            const sortedGoodsList = goodsList
              .filter((el) => el.place !== SHIPPING_PLACE_CODE)
              .sort((a, b) => a.priority - b.priority);
            // @ts-expect-error TS2345
            setPurchasedGoodsStatus(sortedGoodsList);
          }
        }
      }
    } catch (e) {
      setIsFireStorePermissionError(true);
      console.log(e);
    }
  };

  // @ts-expect-error TS7006
  const fetchEventInfo = async (eid) => {
    const eventDocument = await fetchFirestoreDocument<Event>(
      `/events/${eid}/`
    );
    if (eventDocument) {
      const eventDataList = {
        eventName: eventDocument.title,
        eventDate: getSlashSeparatedDateTimeStringFromFbTimestamp(
          eventDocument.datetime
        ),
      };
      setEventStatus(eventDataList);
    }
  };

  const makePathDecodedJSON = (value: string) => {
    setIsReadError(false);
    try {
      const decoded = decodeTicketQRCodeValue(value);
      if (!decoded) {
        setIsReadError(true);
        return;
      }
      const { ticketType, eventId, ticketNumber, authCode, orderId, ticketId } =
        decoded;
      if (
        ticketType === undefined ||
        eventId === undefined ||
        ticketNumber === undefined ||
        authCode === undefined
      ) {
        setIsReadError(true);
        return;
      }
      const ticketTypeResult = z
        .enum(["isChecked", "isRedeemed", "isGoods"])
        .safeParse(ticketType);
      if (!ticketTypeResult.success) {
        setIsReadError(true);
        return;
      }

      return {
        ticketPath: `/tickets/${eventId}/issuedTickets/${ticketNumber}`,
        eventId,
        ticketNumber,
        ticketType: ticketTypeResult.data,
        authCode,
        orderId,
        ticketId,
      };
    } catch (e) {
      const hasMessage = (e: unknown): e is { message: string } =>
        // @ts-expect-error TS18047
        typeof e === "object" && "message" in e;
      // @ts-expect-error TS2769
      throw new DecordingTypeError(hasMessage(e) && e.message);
    }
  };

  const checkTicket = async (
    qrcodeData: NonNullable<ReturnType<typeof makePathDecodedJSON>>
  ) => {
    try {
      if (qrcodeData.ticketType !== "isChecked") {
        dispatch(
          loadingActions.toggleLoading({ msg: t("qrcode.loadingQrcode") })
        );
      }

      const ticketNumber = qrcodeData?.ticketNumber;

      if (!ticketNumber) {
        setDialogMessage("❌チケット番号が取得できませんでした");
        setIsDialogOpen(true);
        return;
      }
      let issuedTicket = issuedTicketMap[ticketNumber];

      // 存在しない場合には、Firestoreから直接データを取得して確認する（リアルタイム同期の遅延が発生した場合）
      if (issuedTicket === undefined) {
        const _issuedTicket = await dbFirestore
          .issuedTickets(qrcodeData.eventId)
          .doc(qrcodeData.ticketNumber)
          .get()
          .then((doc) => doc.data());

        if (_issuedTicket) {
          issuedTicket = _issuedTicket;
        }
      }

      if (!issuedTicket) {
        setDialogMessage("❌該当のチケットが存在しませんでした");
        setIsDialogOpen(true);
        return;
      }

      // check if doc exist / authCodeの認証
      if (
        qrcodeData.authCode &&
        qrcodeData.authCode !== issuedTicket.authCode
      ) {
        throw new DecordingTypeError("Authcode error");
      }

      if (qrcodeData && queryEventId && queryEventId !== qrcodeData.eventId) {
        setDialogMessage("❌対象外のイベントのQRコードです");
        setIsDialogOpen(true);
        return;
      }
      if (
        qrcodeData &&
        queryTicketId &&
        queryTicketId !== qrcodeData.ticketId
      ) {
        setDialogMessage("❌対象外のチケットのQRコードです");
        setIsDialogOpen(true);
        return;
      }

      if (qrcodeData.ticketType === "isChecked") {
        /**
         * とりあえずチケットもぎりだけで検証
         * TODO: 特典引換, グッズ引換のQRコードも同様に検証する必要がある
         */
        if (selectedRedeemTicketIds === undefined) {
          setDialogMessage(
            "❌もぎり対象のチケットIDの取得がまだ完了していません"
          );
          setIsDialogOpen(true);
          return;
        }

        if (
          selectedRedeemTicketIds.length > 0 &&
          qrcodeData?.ticketId &&
          !selectedRedeemTicketIds.includes(qrcodeData.ticketId)
        ) {
          setDialogMessage("❌対象外のチケットのQRコードです");
          setIsDialogOpen(true);
          return;
        }
      }

      if (qrcodeData.ticketType === "isChecked" && !issuedTicket.isChecked) {
        const settingTicketData = {
          isChecked: true,
          checkedDateTime: firebase.firestore.FieldValue.serverTimestamp(),
          whoChecked: uid,
          qrcodeReaderId: getReaderId() !== "" ? getReaderId() : null,
        };

        /**
         * voidにすることでオフライン時にupdateFirestoreDocのPromiseの完了を待たずに処理を進める
         * オフラインからオンラインに復帰した際に、Firestoreが自動的にローカルの変更をサーバーに反映してくれる
         */
        void updateFirestoreDoc(qrcodeData.ticketPath, settingTicketData);

        void playSuccessSound();
        setDialogMessage(`✅${t("qrcode.loadQrcode")}`);
        setIsDialogOpen(true);

        setTimeout(() => {
          setIsDialogOpen(false);
        }, 2000);
        return;
      } else if (
        (qrcodeData.ticketType === "isRedeemed" ||
          qrcodeData.ticketType === "isGoods") &&
        !issuedTicket.isRedeemed
      ) {
        const settingTicketData = {
          isRedeemed: true,
          whoRedeemed: uid,
          redeemedDateTime: firebase.firestore.FieldValue.serverTimestamp(),
        };

        await updateFirestoreDoc(qrcodeData.ticketPath, settingTicketData);
        setDialogMessage(
          `${
            qrcodeData.ticketType === "isGoods"
              ? t("qrcode.goods")
              : t("qrcode.redeem")
          }${t("qrcode.checkQrcode")}`
        );
        setIsDialogOpen(true);
        return;
      } else {
        setDialogMessage(`⚠️${t("qrcode.useTicket")}`);
        setIsDialogOpen(true);
        return;
      }
    } catch (e) {
      if (e instanceof DecordingTypeError) {
        setDialogMessage(`❌${t("qrcode.errorQrcode1")}`);
        setIsDialogOpen(true);
        console.log(e.message);
      } else if (isOfflineError(e)) {
        setDialogMessage(
          `❌同期されたデータには該当のチケットが存在しませんでした。また、オフラインのため、チケット情報を取得できませんでした。通信環境をご確認ください。`
        );
        setIsDialogOpen(true);
        console.log(e);
      } else {
        setDialogMessage(`❌${t("qrcode.errorQrcode2")}`);
        setIsDialogOpen(true);
        console.log(e);
      }
    } finally {
      //読み込んだ後に初期化
      setQrcodeRowData("");
      // @ts-expect-error TS2345
      setPurchasedGoodsStatus(null);
      // @ts-expect-error TS2345
      setQrcodeJson(null);
      // @ts-expect-error TS2345
      setEventStatus(null);
      setIsReadError(false);
      if (qrcodeData.ticketType !== "isChecked") {
        dispatch(loadingActions.toggleLoading({}));
      }
    }
  };

  if (isPrint) {
    return (
      <QrcodePrint
        // @ts-expect-error TS2322
        eventStatus={eventStatus}
        // @ts-expect-error TS2322
        purchasedGoodsStatus={purchasedGoodsStatus}
        closePrint={() => setIsPrint(false)}
      />
    );
  }

  if (!(uid && !isFireStorePermissionError)) {
    return (
      <div css={classesQrcodeStyle.root}>
        <Global styles={globalStyle} />
        <div css={classesQrcodeStyle.headline}>
          <h2>QRCODE READER</h2>
        </div>

        <p css={classesQrcodeStyle.cautionText}>{t("qrcode.noLogin")}</p>
        <div
          css={css`
            max-width: 500px;
            margin: 0 auto;
          `}
        >
          <Button
            text={t("qrcode.login")}
            onClick={() => {
              dispatch(modalActions.toggleLoginMethod());
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div css={classesQrcodeStyle.root}>
      <Global styles={globalStyle} />
      <FullScreenDialog open={isDialogOpen}>
        <div css={successDialogStyle.container}>
          <p css={successDialogStyle.message}>{dialogMessage}</p>
          <div css={successDialogStyle.buttonWrapper}>
            <Button text="確認完了" onClick={() => setIsDialogOpen(false)} />
          </div>
        </div>
      </FullScreenDialog>
      <div css={classesQrcodeStyle.headline}>
        <h2>QRCODE READER</h2>
      </div>

      <div css={classesQrcodeStyle.qrcodeReader}>
        <QrReader delay={300} onError={handleError} onScan={handleScan} />
      </div>

      {isReadError && <p>{t("qrcode.errorQrcode1")}</p>}

      {qrcodeJson && qrcodeJson.ticketType === "isGoods" && (
        <React.Fragment>
          {purchasedGoodsStatus && (
            <QrcodeInfo
              // @ts-expect-error TS2322
              eventStatus={eventStatus}
              purchasedGoodsStatus={purchasedGoodsStatus}
            />
          )}

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto 16px;
            `}
          >
            <Button
              text={
                !(qrcodeRowData && purchasedGoodsStatus)
                  ? t("qrcode.loading")
                  : t("qrcode.cutTicket")
              }
              disabled={!purchasedGoodsStatus}
              onClick={async () => checkTicket(qrcodeJson)}
            />
          </div>

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto 16px;
            `}
          >
            <Button
              text={"チケットを印刷する"}
              onClick={() => setIsPrint(true)}
            />
          </div>

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto;
            `}
          >
            <Button
              text={t("qrcode.reset")}
              color={"#E84B0F"}
              disabled={!(qrcodeRowData && qrcodeJson)}
              onClick={initialState}
            />
          </div>
        </React.Fragment>
      )}
      {/* 特典引換のQRコード読み取り時は、とりあえず今までのままのUIにしておく */}
      {qrcodeJson && qrcodeJson.ticketType === "isRedeemed" && (
        <div
          css={css`
            max-width: 500px;
            margin: 0 auto;
          `}
        >
          <Button
            text={!qrcodeRowData ? t("qrcode.loading") : t("qrcode.cutTicket")}
            disabled={!qrcodeJson}
            onClick={async () => checkTicket(qrcodeJson)}
          />
        </div>
      )}
    </div>
  );
};

const globalStyle = css`
  #container {
    background-color: #000;
  }
`;

const qrcodeStyle = () => {
  return {
    root: css`
      width: 90%;
      max-width: 980px;
      min-height: calc(100vh - 255px);
      padding: 32px 0 80px;
      margin: 0 auto;
    `,
    headline: css`
      margin-bottom: 40px;
      letter-spacing: 1px;
      @media screen and (min-width: 768px) {
        display: flex;
        align-items: center;
        margin-bottom: 60px;
      }
      h2 {
        font-family: "din-condensed", sans-serif;
        font-weight: 400;
        font-style: normal;
        padding: 5px 0 0 15px;
        margin-bottom: 5px;
        color: #fff;
        font-size: 35px;
        line-height: 1em;
        border-left: 5px solid #00c2ae;
        @media screen and (min-width: 768px) {
          margin-bottom: 0;
          letter-spacing: 3px;
        }
      }
    `,
    cautionText: css`
      color: #ffffff;
      font-size: 24px;
      text-align: center;
      padding: 240px 0;
    `,
    infoText: css`
      color: #ffffff;
    `,
    qrcodeReader: css`
      width: 100%;
      max-width: 480px;
      margin: 0 auto 40px;
    `,
  };
};

const successDialogStyle = {
  container: css({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    minHeight: 300,
    gap: 32,
  }),
  message: css({
    fontSize: 24,
    fontWeight: "bold",
    margin: 0,
  }),
  buttonWrapper: css({
    width: "100%",
    maxWidth: 300,
  }),
};

export default QrcodeReader;
